import { api } from './api';
import type {
  Contract,
  ContractSummary,
  ContractListResponse,
  Milestone,
  MilestoneSubmission,
  MilestoneReview,
  Dispute,
  DisputeResponse,
  ContractMessage,
  MessageListResponse,
  CreateContractRequest,
  UpdateContractRequest,
  SignContractRequest,
  SubmitMilestoneRequest,
  ReviewSubmissionRequest,
  CreateDisputeRequest,
  RespondToDisputeRequest,
  SendMessageRequest,
  FundMilestoneRequest,
  ContractStatus,
} from '../types/contract';

// ============================================================================
// Contract CRUD
// ============================================================================

/**
 * Create a new contract with milestones
 */
export async function createContract(
  data: CreateContractRequest
): Promise<Contract> {
  const response = await api.post('/contracts', data);
  return response.data;
}

/**
 * Get a contract by ID
 */
export async function getContract(id: string): Promise<Contract> {
  const response = await api.get(`/contracts/${id}`);
  return response.data;
}

/**
 * Update a draft contract
 */
export async function updateContract(
  id: string,
  data: UpdateContractRequest
): Promise<Contract> {
  const response = await api.put(`/contracts/${id}`, data);
  return response.data;
}

/**
 * List contracts for the current user
 */
export async function listContracts(params?: {
  page?: number;
  page_size?: number;
  status?: ContractStatus;
}): Promise<ContractListResponse> {
  const response = await api.get('/contracts', {
    params: {
      page: params?.page || 1,
      page_size: params?.page_size || 20,
      status: params?.status,
    },
  });
  return response.data;
}

// ============================================================================
// Contract Signing
// ============================================================================

/**
 * Send contract for signatures (company action)
 */
export async function sendContractForSigning(id: string): Promise<Contract> {
  const response = await api.post(`/contracts/${id}/send`);
  return response.data;
}

/**
 * Sign a contract
 */
export async function signContract(
  id: string,
  data: SignContractRequest
): Promise<Contract> {
  const response = await api.post(`/contracts/${id}/sign`, data);
  return response.data;
}

/**
 * Cancel a contract
 */
export async function cancelContract(
  id: string,
  reason: string
): Promise<void> {
  await api.post(`/contracts/${id}/cancel`, { reason });
}

// ============================================================================
// Milestones
// ============================================================================

/**
 * Get milestones for a contract
 */
export async function getMilestones(contractId: string): Promise<Milestone[]> {
  const response = await api.get(`/contracts/${contractId}/milestones`);
  return response.data;
}

/**
 * Get a single milestone with submissions
 */
export async function getMilestone(
  contractId: string,
  milestoneId: string
): Promise<Milestone> {
  const response = await api.get(
    `/contracts/${contractId}/milestones/${milestoneId}`
  );
  return response.data;
}

/**
 * Start working on a milestone (expert action)
 */
export async function startMilestone(
  contractId: string,
  milestoneId: string
): Promise<Milestone> {
  const response = await api.post(
    `/contracts/${contractId}/milestones/${milestoneId}/start`
  );
  return response.data;
}

/**
 * Submit work for a milestone (expert action)
 */
export async function submitMilestone(
  contractId: string,
  milestoneId: string,
  data: SubmitMilestoneRequest
): Promise<MilestoneSubmission> {
  const response = await api.post(
    `/contracts/${contractId}/milestones/${milestoneId}/submit`,
    data
  );
  return response.data;
}

/**
 * Review a milestone submission (company action)
 */
export async function reviewSubmission(
  contractId: string,
  milestoneId: string,
  submissionId: string,
  data: ReviewSubmissionRequest
): Promise<MilestoneReview> {
  const response = await api.post(
    `/contracts/${contractId}/milestones/${milestoneId}/submissions/${submissionId}/review`,
    data
  );
  return response.data;
}

/**
 * Fund escrow for a milestone (company action)
 */
export async function fundMilestoneEscrow(
  contractId: string,
  milestoneId: string,
  data: FundMilestoneRequest
): Promise<Milestone> {
  const response = await api.post(
    `/contracts/${contractId}/milestones/${milestoneId}/fund`,
    data
  );
  return response.data;
}

/**
 * Approve and release payment for a milestone (company action)
 */
export async function approveMilestonePayment(
  contractId: string,
  milestoneId: string
): Promise<Milestone> {
  const response = await api.post(
    `/contracts/${contractId}/milestones/${milestoneId}/pay`
  );
  return response.data;
}

// ============================================================================
// Disputes
// ============================================================================

/**
 * Create a dispute on a contract
 */
export async function createDispute(
  contractId: string,
  data: CreateDisputeRequest
): Promise<Dispute> {
  const response = await api.post(`/contracts/${contractId}/disputes`, data);
  return response.data;
}

/**
 * Get disputes for a contract
 */
export async function getDisputes(contractId: string): Promise<Dispute[]> {
  const response = await api.get(`/contracts/${contractId}/disputes`);
  return response.data;
}

/**
 * Get a dispute with responses
 */
export async function getDispute(
  contractId: string,
  disputeId: string
): Promise<Dispute> {
  const response = await api.get(
    `/contracts/${contractId}/disputes/${disputeId}`
  );
  return response.data;
}

/**
 * Respond to a dispute
 */
export async function respondToDispute(
  contractId: string,
  disputeId: string,
  data: RespondToDisputeRequest
): Promise<DisputeResponse> {
  const response = await api.post(
    `/contracts/${contractId}/disputes/${disputeId}/respond`,
    data
  );
  return response.data;
}

// ============================================================================
// Messages
// ============================================================================

/**
 * Send a message in contract thread
 */
export async function sendMessage(
  contractId: string,
  data: SendMessageRequest
): Promise<ContractMessage> {
  const response = await api.post(`/contracts/${contractId}/messages`, data);
  return response.data;
}

/**
 * Get messages for a contract
 */
export async function getMessages(
  contractId: string,
  page: number = 1,
  pageSize: number = 50
): Promise<MessageListResponse> {
  const response = await api.get(`/contracts/${contractId}/messages`, {
    params: { page, page_size: pageSize },
  });
  return response.data;
}

/**
 * Mark messages as read
 */
export async function markMessagesRead(contractId: string): Promise<void> {
  await api.post(`/contracts/${contractId}/messages/read`);
}

/**
 * Get unread message count
 */
export async function getUnreadCount(
  contractId: string
): Promise<{ unread_count: number }> {
  const response = await api.get(`/contracts/${contractId}/messages/unread`);
  return response.data;
}
